import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
//mui
import { Box, Button, Typography } from '@mui/material';
//components
import ItemImage from '../ItemImage'
import ItemLabel from '../ItemLabel';
import CollapsibleDiv from '../../../CollapsableDiv/CollapsableDiv';
import CommentSection from '../CommentSection';
//slices
import { 
    setCheckboxStates, setItemDescription, handleOpenAuditoryNotesModal, setItemPrice, toggleCommentMode, updateCollapsedStates
} from '../../../../../../store/slices/reportsSlice';
import Checkbox from '../../../../../../components/Checkbox';
//utils
import { capitalizeString } from '../../../../../../utils/main_utils';
//assets
import ddArrow from "../../../../../../assets/icons/dd-arrow.svg";
//constants
import { PROPERTY_ROLE } from '../../../../../../constants';


const COMMENT_MODE = {
    EDIT: 'Edit Comment',
    ADD: 'Add Comment'
};

const getBoxStyles = (commentModes, tabView, itemMetadata, item, editExportMode, finishExportMode) => {
    let currentCommentMode = commentModes[item.object_id] || COMMENT_MODE.ADD;
    // If the item has metadata and the description/price has value, set the comment mode to edit
    if (itemMetadata?.[tabView]?.[item.object_id] && ((itemMetadata[tabView][item.object_id]?.description || "") !== "" || (itemMetadata?.[tabView]?.[item.object_id]?.price || "") !== "")) {
        currentCommentMode = COMMENT_MODE.EDIT;
    }
    const isAddMode = currentCommentMode === COMMENT_MODE.ADD && (editExportMode && !finishExportMode);
    const isEditMode = currentCommentMode === COMMENT_MODE.EDIT && (editExportMode && !finishExportMode);
    
    const boxHeight = isAddMode ? '270px' : 'inherit';
    const boxBackgroundColor = isEditMode ? 'rgba(127, 40, 11, 0.02)' : 'transparent';
    
    return { boxHeight, boxBackgroundColor, currentCommentMode };
};

const AuditoryNotes = ({ header, items, setNotifState }) => {
    const dispatch = useDispatch();
    const { 
        finishExportMode, checkboxStates, report_id, exportReport, tabView, collapsedStates, zoomOutView,
        previewMode, editExportMode, itemMetadata, commentModes, centralizedShowComments, classifiedMetadata
    } = useSelector((state) => state.reports);


    const handleCheckboxChange = (object_id, event) => {
        dispatch(setCheckboxStates({ object_id, checked: event.target.checked }));
    };

    const handleDescriptionChange = (object_id, newValue) => {
        dispatch(setItemDescription({ object_id, description: newValue }));
    };

    const handlePriceChange = (object_id, newValue) => {
        dispatch(setItemPrice({ object_id, price: newValue }));
    };
    
    const toggleComment = (object_id) => {
        dispatch(toggleCommentMode(object_id));
    };

    // Use useMemo to memorize the previewItems calculation
    const previewItems = useMemo(() => (
        previewMode ? 
            items.map((item) => ({
                ...item,
                text: itemMetadata?.[tabView]?.[item.object_id]?.description || item?.text || '',
                description: itemMetadata?.[tabView]?.[item.object_id]?.description || item?.text || '',
                price: itemMetadata?.[tabView]?.[item.object_id]?.price || ''
            })) 
            : 
            items.map((item) => ({ ...item, description: item.text }))
    ), [previewMode, items, itemMetadata]);

    // console.log("At AssetOverview, header: ", header, "\npreviewItems: ", previewItems);

    if (!Array.isArray(previewItems) || previewItems.length === 0) {
        return null;
    }


    return (
        <CollapsibleDiv
            isOpen={collapsedStates.includes(header)}
            minHeight={64}
            maxHeight={1000}
            style={{
                // border: hasDamages ? '2px solid #FF0000' : '1px solid #F2F2F3', 
                border: '1px solid #F2F2F3', 
                borderRadius: '8px',
            }}
        >
            <div className='flex'>
                {(!previewMode && editExportMode) &&
                    <Checkbox
                        checked={previewItems.every((item) => checkboxStates?.[tabView]?.[item.object_id])}
                        onChange={(e) => {
                            previewItems.forEach((item) => handleCheckboxChange(item.object_id, e));
                        }}
                        sx={{ padding: '0', height: '24px', width: '24px', marginRight: '4px' }}
                    />
                }
                <Typography 
                    variant='h6' 
                    sx={{ 
                        fontSize: '16px', 
                        fontWeight: 700, 
                        color: '#0C1222', 
                        marginTop: '2px',
                        marginBottom: '16px' 
                    }}
                >
                    🎙️ Inspector Notes ({previewItems.length})
                </Typography>
                <div className="ml-auto flex">
                    <div className="mt-1">
                        <button 
                            onClick={() => dispatch(updateCollapsedStates({ section: header, collapse: !collapsedStates.includes(header) }))}
                        >
                            <img src={ddArrow} alt="Arrow" style={{ width: '16px', transform: collapsedStates.includes(header) ? 'rotate(180deg)' : '' }} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Grid layout for items */}
            <div 
                className={zoomOutView ?
                    'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 pt-4 pb-8' :
                    'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4 pt-4 pb-8'
                }
            >
                {previewItems.map((item, index) => {
                    const { boxHeight, boxBackgroundColor } = getBoxStyles(commentModes, tabView, itemMetadata, item, editExportMode, finishExportMode);

                    return (
                        <Box
                            key={item.object_id}
                            className={'bg-white rounded-lg shadow-md p-4 flex flex-col' + (item.issue_type === "issue" ? ' border border-red-200' : '')}
                            sx={{ 
                                position: 'relative',
                                overflow: 'hidden',
                                minWidth: '400px',
                                // height: boxHeight,
                                backgroundColor: boxBackgroundColor,
                                maxWidth: zoomOutView ? '800px' : '600px',
                                boxSizing: 'border-box',
                            }}
                        >
                            {item.issue_type === "issue" && (
                                <div 
                                    className='red-gradient-bg'
                                    style={{
                                        position: 'absolute',
                                        top: '-200px',
                                        right: '-350px',
                                        height: '400px',
                                        width: '700px',
                                        zIndex: -1,
                                        background: "linear-gradient(268.33deg, #F9B8D9 17.03%, #FFEBF5 36.31%, #FEF7DF 60.4%, #FFD1E8 85.53%)",
                                        filter: "blur(150px)",
                                        opacity: 0.6,
                                    }}
                                >
                                    <div/>
                                </div>
                            )}
                            <div className="flex">
                                <div
                                    style={{
                                        maxWidth: zoomOutView ? '500px' : '300px', 
                                        minWidth: '200px'
                                    }}
                                >
                                    {/* Image with click handler to open modal */}
                                    <ItemImage
                                        item={item}
                                        itemsSet={previewItems}
                                        index={index}
                                        handleOpenModal={handleOpenAuditoryNotesModal}
                                        dispatch={dispatch}
                                    />

                                    {/* Label and Icon Row */}
                                    <ItemLabel
                                        report_id={report_id}
                                        item={item}
                                        index={index}
                                        previewMode={previewMode}
                                        editExportMode={editExportMode}
                                        finishExportMode={finishExportMode}
                                        checkboxStates={checkboxStates}
                                        tabView={tabView}
                                        showIgnore={false}
                                        handleCheckboxChange={handleCheckboxChange}
                                        dispatch={dispatch}
                                        setNotifState={setNotifState}
                                    />

                                </div>
                                <div className="ml-3">
                                    {/* Item Title */}
                                    <Typography
                                        variant='body2'
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: '#0C1222',
                                            marginBottom: '2px',
                                        }}
                                    >
                                        {(editExportMode || previewMode) ? itemMetadata?.[tabView]?.[item.object_id]?.title || `${capitalizeString(item.issue_type)} Transcription` : `${capitalizeString(item.issue_type)} Transcription`}
                                    </Typography>
                                    
                                    {!previewMode ?
                                        (editExportMode ?
                                            <CommentSection
                                                currentCommentMode={'Edit Comment'}
                                                item={item}
                                                showAutoGen={true}
                                                handleDescriptionChange={handleDescriptionChange}
                                                handlePriceChange={handlePriceChange}
                                                toggleCommentMode={() => toggleComment(item.object_id)}
                                            /> :
                                            <Typography
                                                sx={{
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    color: '#0C1222',
                                                }}
                                            >
                                                {item?.text}
                                            </Typography>
                                        )
                                        :
                                        <>
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    fontWeight: 400,
                                                    color: '#0C1222',
                                                    // opacity: 0.56,
                                                }}
                                            >
                                                {itemMetadata?.[tabView]?.[item.object_id]?.description || ''}
                                            </Typography>
                                            {(!exportReport.currentExportReport?.exported_data?.excludeCost && exportReport.propertyRole === PROPERTY_ROLE.RESIDENT) &&
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                        color: '#0C1222',
                                                        marginTop: '8px',
                                                        // opacity: 0.56,
                                                    }}
                                                >
                                                    Cost: {exportReport.currentExportReport?.exported_data?.currency === 'eur' ? '€' : '$'}{Number((itemMetadata?.[tabView]?.[item.object_id]?.price && itemMetadata?.[tabView]?.[item.object_id]?.price !== "") ? itemMetadata[tabView][item.object_id]?.price : 0).toLocaleString('en-US')}
                                                </Typography>
                                            }
                                        </>
                                    }
                                </div>
                            </div>
                        </Box>
                    );
                })}
            </div>
        </CollapsibleDiv>
    );
};

export default AuditoryNotes;
